import { defineStore, acceptHMRUpdate } from 'pinia';
import i18n from '@/js/plugins/i18n';
import useUiModalsStore from '@/stores/ui/modals';
import useUsersGroupsStore from '@/stores/users/groups';
import useUsersAccessRightStore from '@/stores/users/accessRight';

let firstlogin = true;
let resolveUserPromise;
const userPromise = new Promise(((resolve) => {
  resolveUserPromise = resolve;
}));
let organizationPromise;
let groupsPromise;

const store = defineStore('users', {
  state: () => ({
    user: {},
    userPromise,
    isAnonymous: null,
  }),

  getters: {
    getCompanyUsers() {
      const { user } = this;
      if (! user.id) return [];
      return user.company?.users || [_.pick(user, ['id', 'email', 'firstname', 'lastname', 'avatar', 'access_right', 'meeting_right'])];
    },
    getOrganizationUsers() {
      const accessRightStore = useUsersAccessRightStore();
      let organizationUsers;
      if (accessRightStore.hasOrganization && this.user.organization) {
        organizationUsers = this.user.organization.getUsers();
      } else {
        organizationUsers = this.getCompanyUsers;
      }
      return organizationUsers.slice().sort((a, b) => (window.app.config.globalProperties.$filters.username(a) < window.app.config.globalProperties.$filters.username(b) ? -1 : 1));
    },
    usersById() {
      return this.getOrganizationUsers.reduce((acc, item) => { acc[item.id] = item; return acc; }, {});
    },
    getUserById() {
      return id => this.usersById[id] || null;
    },
    getOrganizationUsernames() {
      return this.getOrganizationUsers.reduce((acc, user) => {
        acc[user.id] = window.app.config.globalProperties.$filters.username(user);
        return acc;
      }, {});
    },
    getOrganizationShortUsernames() {
      return this.getOrganizationUsers.reduce((acc, user) => {
        acc[user.id] = window.app.config.globalProperties.$filters.username(user, 'short');
        return acc;
      }, {});
    },
    getUsername() {
      return (userObj, format) => {
        if (! userObj) return "";
        const username = format == 'short' ? this.getOrganizationShortUsernames[userObj.id] : this.getOrganizationUsernames[userObj.id];
        return username || window.app.config.globalProperties.$filters.username(userObj, format);
      };
    },
    getOrganizationPromise() {
      const accessRightStore = useUsersAccessRightStore();
      return this.userPromise.then(() => {
        if (accessRightStore.hasOrganization) {
          return organizationPromise?.catch(() => null);
        }
        return null;
      });
    },
    getGroupsPromise() {
      const accessRightStore = useUsersAccessRightStore();
      return this.userPromise.then(() => {
        if (accessRightStore.hasGroups) {
          return groupsPromise?.catch(() => null);
        }
        return null;
      });
    },
    getOrganizationSubCompanies() {
      if (! this.user.organization) return this.user.company ? [this.user.company] : [];
      let myOrganization;
      this.user.organization.forEachSubOrga(this.user.organization, (orga) => {
        if (orga.company_id == this.user.company_id) myOrganization = orga;
      });
      if (! myOrganization) return this.user.company ? [this.user.company] : [];
      const subcompanies = [];
      this.user.organization.forEachSubOrga(myOrganization, (orga) => {
        if (orga && orga.company) subcompanies.push(orga.company);
      });
      return subcompanies;
    },
  },

  actions: {
    reset() {
      this.user = {};
    },

    setUser(data) {
      _.forEach(data, (val, key) => {
        this.user[key] = val;
      });

      // update user in organization and company
      const userData = _.pick(data, ['id', 'email', 'firstname', 'lastname', 'avatar', 'access_right', 'meeting_right']);
      const accessRightStore = useUsersAccessRightStore();
      if (accessRightStore.hasOrganization && this.user.organization) {
        const organizationUser = this.user.organization.getUsers().find(user => user.id == this.user.id);
        if (organizationUser) {
          _.forEach(userData, (val, key) => {
            organizationUser[key] = val;
          });
        }
      }
      const companyUser = this.user.company?.users.find(user => user.id == this.user.id);
      if (companyUser) {
        _.forEach(userData, (val, key) => {
          companyUser[key] = val;
        });
      }
    },

    getMe(silent) {
      return window.apiSrv.call('users/me', 'index', null, silent).then(async (result) => {
        const data = result?.data || {};
        const userDefaults = { firstname: i18n.global.t('GLOBAL.FIRSTNAME'), lastname: i18n.global.t('GLOBAL.LASTNAME') };
        this.setUser(_.extend({}, userDefaults, data.user));

        const accessRightStore = useUsersAccessRightStore();
        const groupsStore = useUsersGroupsStore();

        await accessRightStore.load(this.user);

        if (accessRightStore.hasGroups) {
          groupsPromise = groupsStore.load();
        }

        if (accessRightStore.hasOrganization) {
          organizationPromise = window.apiSrv.call('organization', 'index').then((organizationResult) => {
            const organization = organizationResult?.data?.organization || {};
            const companies = [];
            const users = [];
            organization.forEachSubOrga = function (orga, callback, level = 0) {
              callback(orga, level);
              if (! orga.companies) return;
              orga.companies.forEach((subOrga) => {
                organization.forEachSubOrga(subOrga, callback, level + 1);
              });
            };

            organization.forEachSubOrga(organization, (orga, level) => {
              if (! orga || ! orga.company) return;
              orga.company.level = level;
              companies.push(orga.company);
              orga.company.users = orga.company.users.sort((a, b) => (window.app.config.globalProperties.$filters.username(a) < window.app.config.globalProperties.$filters.username(b) ? -1 : 1));
              users.push(...orga.company.users);
            });

            this.user.organization = organization;
            this.user.organization.getUsers = function () {
              return users;
            };
            this.user.organization.getCompanies = function () {
              return companies;
            };
            return this.user.organization;
          });
        }

        if (firstlogin) resolveUserPromise(this.user);

        if (this.user.company?.isPremiumExpired) {
          useUiModalsStore().setPremiumSuspended(true);
        } else if (firstlogin) {
          const userCreatedAt = moment(this.user.created_at);
          const isUserFirstminutes = moment().add(-10, 'minutes').isBefore(userCreatedAt) && userCreatedAt.isBefore();
          if (isUserFirstminutes) useUiModalsStore().setOnBoardingWelcomeModal(true);
        }
        if (this.user.company?.isPremium && moment(this.user.company.premiumExpireDate).endOf('day').isBefore()) {
          useUiModalsStore().setPremiumExpired(true);
        }
        this.isAnonymous = false;
        firstlogin = false;
        return this.user;
      });
    },

    getMeOrRetry(retryDelay = 300) {
      this.getMe(true).catch(async (message) => {
        const { default: vueApp } = await import('@/js/vueApp');
        const query = vueApp?.config.globalProperties.$route.query || {};
        if (query.rotoken || message == 'Not logged in' || message == 'Session expired') {
          this.isAnonymous = true;
          return; // unlogued access
        }
        window.setTimeout(() => { this.getMeOrRetry(Math.min(retryDelay * 2, 60000)); }, retryDelay);
      });
    },

    /** *************************** */
    /** ** INIT + PING / REFRESH ** */
    /** *************************** */
    load() {
      this.getMeOrRetry();
      setInterval(() => {
        if (! this.user.id) return;
        this.getMe(true).catch(() => {});
      }, 3600000);
    },
  },
});

if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(store, import.meta.hot));
export default store;
